import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Alert, Spin, message, theme } from "antd";
import {
  cancelBillingPolicyApi,
  getAllBillingPoliciesApi,
} from "../../../../../services/billingPolicies.services";
import PolicyList from "./PolicyList";
import PolicyConfigure from "./PolicyConfigure";
import primaryTints from "./primaryTints";
import { apiError, normalizePolicyGroups } from "./policyUtils";
import styles from "./BillingPolicies.module.css";

/* Facility-wise billing policies with the version history behind each one.
   The list opens on every facility at once; Configure opens the editor with a
   live test beside it. */
const BillingPolicies = () => {
  const { token } = theme.useToken();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [configuringId, setConfiguringId] = useState(null);
  const [flashId, setFlashId] = useState(null);

  const tints = useMemo(() => primaryTints(token.colorPrimary), [token.colorPrimary]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getAllBillingPoliciesApi();
      setGroups(normalizePolicyGroups(res));
      setLoadError(null);
    } catch (e) {
      setGroups([]);
      setLoadError(apiError(e, "Failed to load billing policies"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const configuring = groups.find((group) => group.id === configuringId) || null;

  const cancelVersion = async (group, version) => {
    try {
      await cancelBillingPolicyApi(version.id);
      message.success(
        `v${version.version} cancelled for ${group.facility.name} — the policy in force stays as it is`
      );
      await load();
    } catch (e) {
      message.error(apiError(e, "Couldn't cancel this version"));
    }
  };

  const afterSchedule = async (facilityId) => {
    setConfiguringId(null);
    setFlashId(facilityId);
    window.scrollTo(0, 0);
    await load();
  };

  return (
    <div className={styles.container} style={tints}>
      {loading ? (
        <div className={styles.loading}>
          <Spin />
        </div>
      ) : loadError ? (
        <Alert type="error" showIcon message={loadError} />
      ) : configuring ? (
        <PolicyConfigure
          key={configuring.id}
          group={configuring}
          onBack={() => {
            setConfiguringId(null);
            window.scrollTo(0, 0);
          }}
          onScheduled={afterSchedule}
        />
      ) : (
        <PolicyList
          key={flashId || "list"}
          groups={groups}
          flashFacilityId={flashId}
          onConfigure={(group) => {
            setFlashId(null);
            setConfiguringId(group.id);
            window.scrollTo(0, 0);
          }}
          onCancel={cancelVersion}
        />
      )}
    </div>
  );
};

export default BillingPolicies;
